/**
 * How a stay's dates are written in lists.
 *
 * <p>Day and month only. A manager scanning a week of bookings already knows the year, and
 * repeating it on every row costs width in a cell that has to stay narrow — the reservations table
 * keeps this column on one unwrapped line.
 *
 * <p>The year comes back whenever leaving it out would be ambiguous, which is two cases at once:
 * a stay outside the current year, and a stay that crosses out of it. "31. 12. → 2. 1." invites you
 * to read three days as minus-364; a bare "12. 9." on next season's booking says nothing about which
 * September. Both reduce to a single question — is every end of this stay in the year we are in now.
 */

/**
 * True unless both ends fall in the current calendar year.
 *
 * <p>Compares the year off the ISO string (YYYY-MM-DD) rather than parsing to a Date: in a
 * negative-offset timezone, `new Date('2027-01-01')` lands on 31 December of the previous year and
 * would drop the year from exactly the booking that needs it most.
 */
export const needsYear = (checkIn?: string | null, checkOut?: string | null): boolean => {
  if (!checkIn || !checkOut) return false;
  const thisYear = String(new Date().getFullYear());
  return checkIn.slice(0, 4) !== thisYear || checkOut.slice(0, 4) !== thisYear;
};

/**
 * One end of a stay. Pass the same `withYear` for both ends of a pair — a range with the year on
 * one side only reads as a mistake.
 *
 * <p>Locale is left to the browser, exactly as the `toLocaleDateString()` calls this replaces did,
 * so Czech still renders "12. 9." and English "12/09".
 */
export const formatStayDate = (dateStr: string, withYear: boolean): string =>
  new Date(dateStr).toLocaleDateString(
    undefined,
    withYear
      ? { day: 'numeric', month: 'numeric', year: 'numeric' }
      : { day: 'numeric', month: 'numeric' },
  );

/** Both ends of a stay, with the year applied to both or neither. */
export const formatStayRange = (checkIn: string, checkOut: string): { from: string; to: string } => {
  const withYear = needsYear(checkIn, checkOut);
  return { from: formatStayDate(checkIn, withYear), to: formatStayDate(checkOut, withYear) };
};
